import { useEffect, useState } from "react";
import { mostraAlertaError, mostraAlertaOk } from "../../../componentes/alerts/sweetAlert";
import { AxiosPublico } from "../../../componentes/axios/Axios";
import Card from "../../../componentes/contenedores/Card";
import Page from "../../../componentes/plantilla/Page";
import { AccesosGuardar, AccesosListar, UsuariosEmpleadosListar } from "../../../configuracion/apiUrls";
import { useContextUsuario } from "../../../contexto/usuario/UsuarioContext";
import { permisosPorRuta } from "../../../configuracion/Empresa";

const getPermisosString = (permisos) => {
    if (Array.isArray(permisos)) {
        return permisos.slice().sort().join(",");
    } else if (typeof permisos === "string") {
        return permisos.split(",").map(p => p.trim()).sort().join(",");
    } else {
        return "";
    }
};

const HomeAccesos = () => {
    const { usuario } = useContextUsuario();

    const datos = {
        titulo: {
            titulo: "Nuevo acceso",
            url: "/app/admin/empleados/accesos",
            tituloUrl: "Administración de accesos",
            nombreUrl: "Inicio ",
        },
    };

    const [empleados, setEmpleados] = useState([]);
    const [form, setForm] = useState({ usuarioId: 0, accesos: [] });
    const [accesosPrevios, setAccesosPrevios] = useState([]);
    const [areaSeleccionada, setAreaSeleccionada] = useState("");

    useEffect(() => {
        cargarEmpleados();
    }, []);

    const cargarEmpleados = async () => {
        try {
            const res = await AxiosPublico.get(UsuariosEmpleadosListar);
            const empleadosFiltrados = res.data.filter(u => u.id !== usuario.id);
            setEmpleados(empleadosFiltrados);
        } catch (error) {
            console.error("Error al cargar empleados:", error);
        }
    };

    const cargarAccesosPorUsuario = async (usuarioId) => {
        try {
            const res = await AxiosPublico.get(AccesosListar);
            const accesosUsuario = res.data.filter(acc => acc.usuarioId === usuarioId);
            const accesos = accesosUsuario.map(acc => ({
                ruta: acc.ruta,
                permisos: typeof acc.permisos === "string"
                    ? acc.permisos.split(",").map(p => p.trim())
                    : acc.permisos || [],
                estado: acc.estado
            }));
            setForm({ usuarioId, accesos });
            setAccesosPrevios(accesos);
        } catch (error) {
            console.error("Error al cargar accesos:", error);
        }
    };

    const togglePermiso = (ruta, permiso) => {
        const updatedAccesos = form.accesos.map(acc => {
            if (acc.ruta === ruta) {
                const permisos = acc.permisos.includes(permiso)
                    ? acc.permisos.filter(p => p !== permiso)
                    : [...acc.permisos, permiso];
                return { ...acc, permisos };
            }
            return acc;
        });
        setForm({ ...form, accesos: updatedAccesos });
    };

    const toggleRuta = (ruta) => {
        const existe = form.accesos.find(acc => acc.ruta === ruta);
        if (existe) {
            return;
        }
        setForm({ ...form, accesos: [...form.accesos, { ruta, permisos: [], estado: "Activo" }] });
    };

    const handleGuardarAccesos = async () => {
        if (!hayCambios) {
            mostraAlertaError("Sin cambios", "No hay cambios para guardar.");
            return;
        }
        try {
            const data = {
                usuarioId: form.usuarioId,
                accesos: form.accesos.map(acc => ({
                    ruta: acc.ruta,
                    permisos: acc.permisos,
                    estado: acc.estado
                }))
            };
            await AxiosPublico.post(AccesosGuardar, data);
            mostraAlertaOk("Accesos procesados correctamente");
            setAccesosPrevios([...form.accesos]);
        } catch (error) {
            mostraAlertaError("Error al guardar", "No se pudieron procesar los accesos");
            console.error(error);
        }
    };

    // Determinar nuevos y editados en cada render
    const nuevosAccesos = form.accesos.filter(acc => !accesosPrevios.find(prev => prev.ruta === acc.ruta));
    const accesosEditados = form.accesos.filter(acc => {
        const prev = accesosPrevios.find(prev => prev.ruta === acc.ruta);
        if (!prev) return false;
        const permisosNuevo = getPermisosString(acc.permisos);
        const permisosPrev = getPermisosString(prev.permisos);
        return acc.estado !== prev.estado || permisosNuevo !== permisosPrev;
    });
    const hayCambios = nuevosAccesos.length > 0 || accesosEditados.length > 0;

    return (
        <Page datos={datos}>
            <Card titulo="Gestión de accesos">
                <div className="mb-4">
                    <label className="form-label fw-bold">Seleccionar empleado:</label>
                    <div className="row">
                        <div className="col-md-8 col-lg-6">
                            <select
                                className="form-select bg-light border-0 rounded-2 shadow-sm px-3 py-2"
                                value={form.usuarioId}
                                onChange={(e) => cargarAccesosPorUsuario(Number(e.target.value))}
                            >
                                <option value={0}>Seleccione un empleado</option>
                                {empleados.map(usuarioItem =>
                                    usuarioItem.Empleados.length > 0 && (
                                        <option
                                            key={usuarioItem.id}
                                            value={usuarioItem.id}
                                            title={usuarioItem.Empleados[0].nombreCompleto}
                                        >
                                            {usuarioItem.Empleados[0].nombreCompleto}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>
                    </div>
                </div>

                {form.usuarioId !== 0 && (
                    <div className="fade show">
                        {/* Accesos disponibles */}
                        <div className="border rounded shadow-sm p-3 mb-4 bg-white">
                            <h5 className="mb-3 fw-bold">Accesos disponibles</h5>
                            <div className="row gx-3 gy-3">
                                {Object.entries(permisosPorRuta).map(([nombre, { ruta }]) => (
                                    <div key={ruta} className="col-6 col-sm-4 col-md-3 col-lg-2 my-1">
                                        <button
                                            type="button"
                                            className={`btn w-100 text-truncate transition ${areaSeleccionada === ruta ? 'btn-primary' : 'btn-outline-primary'}`}
                                            onClick={() => setAreaSeleccionada(ruta)}
                                        >
                                            {areaSeleccionada === ruta ? "✓ " : ""}{nombre}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Configuración de permisos */}
                        {areaSeleccionada && (() => {
                            const entry = Object.entries(permisosPorRuta).find(([_, v]) => v.ruta === areaSeleccionada);
                            if (!entry) return null;
                            const [nombre, { ruta, permisos }] = entry;
                            const selected = form.accesos.find(acc => acc.ruta === ruta);

                            return (
                                <div className="card shadow-sm mb-4 animate__animated animate__fadeIn">
                                    <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="card-title mb-0 text-secondary">
                                                <i className="bi bi-shield-lock-fill me-2"></i>
                                                Permisos para: <span className="text-primary">{nombre}</span>
                                            </h5>
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${selected ? 'btn-secondary disabled' : 'btn-outline-success'}`}
                                                onClick={() => toggleRuta(ruta)}
                                                disabled={!!selected}
                                            >
                                                {selected ? "Acceso agregado" : "Agregar acceso"}
                                            </button>
                                        </div>

                                        {selected && (
                                            <div className="row">
                                                {permisos.map((perm, idx) => (
                                                    <div className="form-check col-md-6 col-lg-4 mb-3" key={idx}>
                                                        <input
                                                            className="form-check-input rounded shadow-sm"
                                                            type="checkbox"
                                                            checked={selected.permisos.includes(perm)}
                                                            onChange={() => togglePermiso(ruta, perm)}
                                                            id={`${ruta}-${perm}`}
                                                        />
                                                        <label className="form-check-label ms-3 fw-semibold fs-6 text-dark" htmlFor={`${ruta}-${perm}`}>
                                                            {perm}
                                                        </label>
                                                    </div>
                                                ))}
                                                <div className="col-12 mt-3">
                                                    <label className="form-label fw-semibold">Estado del acceso:</label>
                                                    <select
                                                        className="px-3 py-2 mx-2 rounded-md bg-gray-100 text-gray-700 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
                                                        value={selected.estado}
                                                        onChange={(e) => {
                                                            const updated = form.accesos.map(acc =>
                                                                acc.ruta === ruta ? { ...acc, estado: e.target.value } : acc
                                                            );
                                                            setForm({ ...form, accesos: updated });
                                                        }}
                                                    >
                                                        <option value="Activo">Activo</option>
                                                        <option value="Bloqueado">Bloqueado</option>
                                                    </select>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Muestra los cambios (nuevos y editados) aquí */}
                                    {(nuevosAccesos.length > 0 || accesosEditados.length > 0) && (
                                        <div className="p-3">
                                            {nuevosAccesos.length > 0 && (
                                                <div className="border rounded shadow-sm p-3 mb-4 bg-light animate__animated animate__fadeIn">
                                                    <h5 className="mb-3 text-success">Nuevos Accesos</h5>
                                                    <ul className="list-group mb-3">
                                                        {nuevosAccesos.map(acc => (
                                                            <li key={acc.ruta} className="list-group-item d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <strong>
                                                                        {
                                                                            Object.entries(permisosPorRuta).find(([n, v]) => v.ruta === acc.ruta)?.[0]
                                                                            || acc.ruta
                                                                        }
                                                                    </strong>
                                                                    <span className="badge bg-success ms-2 mx-2">{acc.estado}</span>
                                                                    <div className="text-muted small">Permisos: {acc.permisos.join(", ")}</div>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {accesosEditados.length > 0 && (
                                                <div className="border rounded shadow-sm p-3 mb-4 bg-light animate__animated animate__fadeIn">
                                                    <h5 className="mb-3 text-warning">Accesos modificados</h5>
                                                    <ul className="list-group mb-3">
                                                        {accesosEditados.map(acc => (
                                                            <li key={acc.ruta} className="list-group-item d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <strong>
                                                                        {
                                                                            Object.entries(permisosPorRuta).find(([n, v]) => v.ruta === acc.ruta)?.[0]
                                                                            || acc.ruta
                                                                        }
                                                                    </strong>
                                                                    <span className="badge bg-warning text-dark ms-2 mx-2">{acc.estado}</span>
                                                                    <div className="text-muted small">Permisos: {acc.permisos.join(", ")}</div>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    )}


                                </div>
                            );
                        })()}

                        <div className="text-end mt-4">
                            <button
                                className={`btn ${hayCambios ? 'btn-success' : 'btn-secondary'}`}
                                onClick={handleGuardarAccesos}
                                disabled={!hayCambios}
                            >
                                <i className="bi bi-save me-1"></i> Guardar cambios
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </Page>
    );
};

export default HomeAccesos;